<template>
  <div class="DualRangeSlider" ref="sliderRoot">
    <div class="DualRangeSlider__trackWrap" aria-hidden="true">
      <div class="DualRangeSlider__track" />
      <div class="DualRangeSlider__trackFill" :style="trackFillStyle" />
    </div>
    <input
      type="range"
      class="DualRangeSlider__input DualRangeSlider__input--low"
      :min="minBound"
      :max="maxBound"
      :step="step"
      :value="orderedLow"
      :aria-label="ariaLabelLow"
      @input="onLowInput"
    />
    <input
      type="range"
      class="DualRangeSlider__input DualRangeSlider__input--high"
      :min="minBound"
      :max="maxBound"
      :step="step"
      :value="orderedHigh"
      :aria-label="ariaLabelHigh"
      @input="onHighInput"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = withDefaults(
  defineProps<{
    minBound: number
    maxBound: number
    low: number
    high: number
    step?: number
    ariaLabelLow?: string
    ariaLabelHigh?: string
  }>(),
  {
    step: 1,
    ariaLabelLow: 'Minimum',
    ariaLabelHigh: 'Maximum',
  },
)

const emit = defineEmits<{
  'update:low': [value: number]
  'update:high': [value: number]
}>()

const sliderRoot = ref<HTMLElement | null>(null)
/** Bumped when the root is resized or first laid out so fill width recalculates (e.g. accordion opens). */
const layoutTick = ref(0)

let resizeObserver: ResizeObserver | null = null

const THUMB_WIDTH_PX = 18
const THUMB_RADIUS_PX = THUMB_WIDTH_PX / 2

const span = computed(() => Math.max(0, props.maxBound - props.minBound))

const orderedLow = computed(() => Math.min(props.low, props.high))
const orderedHigh = computed(() => Math.max(props.low, props.high))

function fillStylePercent(lo: number, hi: number, s: number): Record<string, string> {
  const leftPercent = ((lo - props.minBound) / s) * 100
  const widthPercent = ((hi - lo) / s) * 100
  return {
    left: `${Math.max(0, Math.min(100, leftPercent))}%`,
    width: `${Math.max(0, Math.min(100, widthPercent))}%`,
  }
}

const trackFillStyle = computed(() => {
  void layoutTick.value
  const s = span.value
  if (s <= 0) {
    return { left: '0%', width: '0%' }
  }
  const lo = orderedLow.value
  const hi = orderedHigh.value
  const el = sliderRoot.value
  const containerWidth = el?.offsetWidth ?? 0
  if (containerWidth <= 0) {
    return fillStylePercent(lo, hi, s)
  }
  const usableWidth = containerWidth - THUMB_WIDTH_PX
  if (usableWidth <= 0) {
    return fillStylePercent(lo, hi, s)
  }
  const leftFraction = (lo - props.minBound) / s
  const widthFraction = (hi - lo) / s
  const leftPx = THUMB_RADIUS_PX + leftFraction * usableWidth
  const widthPx = widthFraction * usableWidth
  return {
    left: `${leftPx}px`,
    width: `${widthPx}px`,
  }
})

function bumpLayoutTick(): void {
  layoutTick.value++
}

onMounted(() => {
  void nextTick(() => bumpLayoutTick())
})

watch(
  sliderRoot,
  (el) => {
    resizeObserver?.disconnect()
    resizeObserver = null
    if (el) {
      resizeObserver = new ResizeObserver(() => bumpLayoutTick())
      resizeObserver.observe(el)
    }
    void nextTick(() => bumpLayoutTick())
  },
  { flush: 'post' },
)

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(
  () => [props.low, props.high] as const,
  ([l, h]) => {
    if (l > h) {
      emit('update:low', Math.min(l, h))
      emit('update:high', Math.max(l, h))
    }
  },
  { flush: 'sync', immediate: true },
)

function onLowInput(event: Event): void {
  const raw = Number((event.target as HTMLInputElement).value)
  const nextMin = Math.max(raw, props.minBound)
  const currentHigh = Math.max(props.low, props.high)
  // Push max forward if min catches up, capped at maxBound.
  const nextMax = Math.max(nextMin, Math.min(currentHigh, props.maxBound))
  emit('update:low', nextMin)
  emit('update:high', nextMax)
}

function onHighInput(event: Event): void {
  const raw = Number((event.target as HTMLInputElement).value)
  const nextMax = Math.min(raw, props.maxBound)
  const currentLow = Math.min(props.low, props.high)
  // Push min backward if max catches up, floored at minBound.
  const nextMin = Math.min(nextMax, Math.max(currentLow, props.minBound))
  emit('update:low', nextMin)
  emit('update:high', nextMax)
}
</script>

<style scoped lang="scss">
/* Two native ranges stacked; pointer-events only on thumbs so both work on one visual track. */
.DualRangeSlider {
  position: relative;
  width: 100%;
  height: 28px;
  margin: 0.15rem 0 0;
}

.DualRangeSlider__trackWrap {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 6px;
  pointer-events: none;
  z-index: 1;
}

.DualRangeSlider__track {
  position: absolute;
  inset: 0;
  background: #e5e7eb;
  border-radius: 3px;
}

.DualRangeSlider__trackFill {
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 3px;
  background: var(--color-main, #025959);
  pointer-events: none;
  z-index: 1;
}

.DualRangeSlider__input {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  height: 28px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  outline: none;
  pointer-events: none;
  /* Kill native track tint (Chrome / Safari); only our .DualRangeSlider__trackFill shows the range. */
  accent-color: transparent;
}

.DualRangeSlider__input--low {
  z-index: 3;
}

.DualRangeSlider__input--high {
  z-index: 2;
}

.DualRangeSlider__input::-webkit-slider-runnable-track {
  /* Transparent; vertical centering of the thumb is done by the browser relative to this track. */
  height: 18px;
  background: transparent;
  border: none;
  box-shadow: none;
}

.DualRangeSlider__input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  pointer-events: auto;
  width: 18px;
  height: 18px;
  /* No margin-top: thumb auto-centers on the track (track = 18px = thumb height). */
  border-radius: 50%;
  background: var(--color-main, #025959);
  border: 2px solid #fff;
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.18),
    0 0 0 1px rgba(0, 0, 0, 0.06);
  cursor: grab;
}

.DualRangeSlider__input:active::-webkit-slider-thumb {
  cursor: grabbing;
}

.DualRangeSlider__input::-moz-range-track {
  height: 6px;
  background: transparent;
  border: none;
}

.DualRangeSlider__input::-moz-range-progress {
  height: 6px;
  background: transparent;
}

.DualRangeSlider__input::-moz-range-thumb {
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-main, #025959);
  border: 2px solid #fff;
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.18),
    0 0 0 1px rgba(0, 0, 0, 0.06);
  cursor: grab;
}

.DualRangeSlider__input:active::-moz-range-thumb {
  cursor: grabbing;
}
</style>
